import { skipToken, useQuery } from '@tanstack/react-query';

import { apiQuery } from '../../state';
import {
    agentHasPendingQuestion,
    agentIsProcessing,
    type AgentStateSource,
    type GitBookAgentClientState,
    useAgentState,
    useChangeRequestAgent,
    useOrganizationAgent,
    useOrganizationBackgroundAgent,
} from '../Agent';
import type { AgentDockTab } from './state';

/**
 * What a tab's conversation is up to, for the tab to report while its window is off screen:
 * `question` when the agent has stopped to ask the user something, `processing` while it works.
 *
 * Each tab watches its conversation as a watcher: the state that says what the agent is doing, and
 * neither the transcript nor the message bodies it would never show. The conversation has one
 * connection whoever asks for it, so opening the tab's window promotes this one rather than adding
 * a second — and holding it here is what keeps a conversation's client alive, with whatever it has
 * still to deliver, while its window is closed.
 */
export function useAgentDockTabStatus(input: {
    organizationId: string;
    tab: AgentDockTab;
}): 'processing' | 'question' | undefined {
    const { organizationId, tab } = input;

    // Both clients are narrowed to the shape this hook actually reads, as they are created.
    // Their RPC stubs are deeply recursive types (capnweb), and carrying them to the call below
    // makes the checker resolve both at once — deep enough to exhaust its instantiation budget and
    // fail an unrelated file.
    const organizationAgent: AgentStateSource = useOrganizationAgent({
        organizationId,
        conversationId: tab.kind === 'organization' ? tab.conversationId : '',
        enabled: tab.kind === 'organization',
        transcript: false,
    });

    // The same gate the window applies before connecting: the space has to be one the user can
    // reach. Editing it is not required — a read-only conversation still processes and still asks
    // questions, and the badge is how a minimized tab says so.
    const changeRequestTab = tab.kind === 'change_request' ? tab : null;
    const { data: space } = useQuery(
        apiQuery(
            'getSpaceById',
            changeRequestTab ? { spaceId: changeRequestTab.spaceId } : skipToken
        )
    );
    const changeRequestAgent: AgentStateSource = useChangeRequestAgent({
        organizationId,
        spaceId: changeRequestTab?.spaceId ?? '',
        changeRequestId: changeRequestTab?.changeRequestId ?? '',
        conversationId: changeRequestTab?.conversationId ?? null,
        enabled: Boolean(changeRequestTab && space),
        transcript: false,
    });

    const backgroundAgent: AgentStateSource = useOrganizationBackgroundAgent({
        organizationId,
        conversationId: tab.kind === 'organization_background' ? tab.conversationId : '',
        enabled: tab.kind === 'organization_background',
        transcript: false,
    });

    const agent =
        tab.kind === 'organization'
            ? (organizationAgent as WatchableAgentState)
            : tab.kind === 'organization_background'
              ? (backgroundAgent as WatchableAgentState)
              : (changeRequestAgent as WatchableAgentState);
    return useAgentState(agent, selectTabStatus);
}

type WatchableAgentState = {
    listen: (event: 'state', callback: (state: GitBookAgentClientState) => void) => () => void;
    state: GitBookAgentClientState;
} | null;

function selectTabStatus(state: GitBookAgentClientState): 'processing' | 'question' | undefined {
    // The question outranks the working state: while one is pending the agent is paused on the
    // user, and that is the thing worth surfacing.
    if (agentHasPendingQuestion(state)) {
        return 'question';
    }
    if (agentIsProcessing(state)) {
        return 'processing';
    }
    return undefined;
}
